import {useSyncExternalStore} from 'use-sync-external-store/shim';

import {safeLocalStorage} from '../util/localStorage';
import type {Thread} from './store/thread';

const STORAGE_KEY = 'wb-agent-thread-read-status';

export interface ReadStatusSnapshot {
  clientBootstrappedAt: number;
  lastViewed: Record<string, number>;
}

let cachedSnapshot: ReadStatusSnapshot = readInitialSnapshot();
const listeners = new Set<() => void>();

export function getReadStatusSnapshot(): ReadStatusSnapshot {
  return cachedSnapshot;
}

export function subscribeReadStatus(notify: () => void): () => void {
  listeners.add(notify);
  return () => {
    listeners.delete(notify);
  };
}

export function markThreadRead(rootTurnId: string, now = Date.now()): void {
  const existing = cachedSnapshot.lastViewed[rootTurnId];
  if (existing != null && existing >= now) {
    return;
  }

  commitSnapshot({
    clientBootstrappedAt: cachedSnapshot.clientBootstrappedAt,
    // TODO: Clean up stale entries (e.g. have useThreadReadTracker drop entries with no
    // newer thread activity when summaries refresh)
    lastViewed: {
      ...cachedSnapshot.lastViewed,
      [rootTurnId]: now,
    },
  });
}

export function isThreadUnread(
  thread: Pick<Thread, 'rootTurnId' | 'status' | 'lastActivityAt'>,
  snapshot: ReadStatusSnapshot = cachedSnapshot
): boolean {
  // Drafts (not yet promoted new chats --> have no rootTurnId) are never unread.
  if (thread.rootTurnId == null) {
    return false;
  }
  if (thread.status === 'running') {
    return false;
  }
  // Including clientBootstrappedAt makes a post-cache-clear reload show zero unread threads.
  const floor = Math.max(
    snapshot.lastViewed[thread.rootTurnId] ?? 0,
    snapshot.clientBootstrappedAt
  );
  // Compare against lastActivityAt (last user/agent message) — not updatedAt —
  // so a metadata-only update like a title rename can't surface the unread dot.
  return thread.lastActivityAt > floor;
}

/**
 * useThreadReadStatus subscribes the calling component to per-thread
 * read-status. The returned `isUnread` closes over the current snapshot;
 * `markRead` is referentially stable.
 */
export function useThreadReadStatus(): {
  isUnread: (
    thread: Pick<Thread, 'rootTurnId' | 'status' | 'lastActivityAt'>
  ) => boolean;
  markRead: (rootTurnId: string) => void;
} {
  const snapshot = useSyncExternalStore(
    subscribeReadStatus,
    getReadStatusSnapshot
  );
  return {
    isUnread: thread => isThreadUnread(thread, snapshot),
    markRead: markThreadRead,
  };
}

function readInitialSnapshot(): ReadStatusSnapshot {
  const parsed = safeLocalStorage.isAvailable
    ? parsePersisted(safeLocalStorage.getItem(STORAGE_KEY))
    : null;
  if (parsed != null) {
    return parsed;
  }

  // First sighting on this client (or storage disabled): stamp bootstrap
  // time and best-effort persist. When storage is off, marks still gate
  // dots for the rest of this session via the in-memory snapshot — they
  // just don't survive a reload.
  const fresh: ReadStatusSnapshot = {
    clientBootstrappedAt: Date.now(),
    lastViewed: {},
  };
  writePersisted(fresh);
  return fresh;
}

function commitSnapshot(next: ReadStatusSnapshot): void {
  writePersisted(next);
  cachedSnapshot = next;
  notifyListeners();
}

function writePersisted(value: ReadStatusSnapshot): void {
  if (!safeLocalStorage.isAvailable) {
    return;
  }
  safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

function parsePersisted(raw: string | null): ReadStatusSnapshot | null {
  if (raw == null) {
    return null;
  }
  // Handle malformed JSON
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed == null ||
      typeof parsed !== 'object' ||
      typeof (parsed as ReadStatusSnapshot).clientBootstrappedAt !== 'number'
    ) {
      return null;
    }
    const candidate = parsed as ReadStatusSnapshot;

    const lastViewed: Record<string, number> = {};
    if (
      candidate.lastViewed != null &&
      typeof candidate.lastViewed === 'object'
    ) {
      for (const [k, v] of Object.entries(candidate.lastViewed)) {
        if (typeof v === 'number' && Number.isFinite(v)) {
          // lastViewed maps every thread the user has opened
          lastViewed[k] = v;
        }
      }
    }
    return {
      clientBootstrappedAt: candidate.clientBootstrappedAt,
      lastViewed,
    };
  } catch {
    return null;
  }
}

function notifyListeners(): void {
  for (const listener of Array.from(listeners)) {
    listener();
  }
}

// Handles cross-tab sync. Another tab's write fires a `storage` event here. Re-parse
// the new value, replace the cached snapshot reference, and notify so
// consumers re-render. `storage` doesn't fire in the writing tab, so
// same-tab notifications go through `commitSnapshot` above.
window.addEventListener('storage', event => {
  if (event.key !== STORAGE_KEY) {
    return;
  }
  const parsed = parsePersisted(event.newValue);
  if (parsed == null) {
    return;
  }
  cachedSnapshot = parsed;
  notifyListeners();
});
